import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { clinicDataApi } from '../services/api';

function formatDateBR(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function ClinicForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState({ leads: '', appointments: '', attendances: '', closings: '' });

  useEffect(() => {
    clinicDataApi.getFormData(token)
      .then(res => {
        if (!res.success) { setError(res.message || 'Link inválido'); return; }
        setFormData(res.data);
        if (res.data.isFilled) setSubmitted(true);
      })
      .catch(() => setError('Erro ao carregar o formulário'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.leads || !values.appointments || !values.attendances) return;
    setSubmitting(true);
    try {
      const res = await clinicDataApi.submitForm(token, {
        leads: parseInt(values.leads, 10),
        appointments: parseInt(values.appointments, 10),
        attendances: parseInt(values.attendances, 10),
        closings: parseInt(values.closings, 10) || 0,
      });
      if (res.success || res.message === 'Formulário já preenchido') {
        setSubmitted(true);
      } else {
        setError(res.message || 'Erro ao salvar');
      }
    } catch {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const getPeriodLabel = () => {
    if (!formData) return '';
    if (formData.periodType === 'DAILY') return `Dia ${formatDateBR(formData.periodStart)}`;
    return `Semana de ${formatDateBR(formData.periodStart)} a ${formatDateBR(formData.periodEnd)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 text-white">
          <div className="flex items-center gap-3 mb-1">
            <span className="material-symbols-outlined text-white/80">local_hospital</span>
            <p className="text-sm font-medium text-white/80 uppercase tracking-wider">Dados da Clínica</p>
          </div>
          {loading ? (
            <div className="h-6 bg-white/20 rounded animate-pulse w-48 mt-2"></div>
          ) : formData ? (
            <>
              <h1 className="text-xl font-bold mt-1">{formData.routineName}</h1>
              <p className="text-sm text-white/70 mt-1">{formData.adAccountName}</p>
            </>
          ) : null}
        </div>

        <div className="px-8 py-6">
          {loading && (
            <div className="space-y-4">
              <div className="h-4 bg-slate-100 rounded animate-pulse"></div>
              <div className="h-12 bg-slate-100 rounded animate-pulse"></div>
              <div className="h-12 bg-slate-100 rounded animate-pulse"></div>
              <div className="h-12 bg-slate-100 rounded animate-pulse"></div>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-8">
              <span className="material-symbols-outlined text-red-400 text-5xl">error</span>
              <p className="mt-3 text-slate-600 font-medium">{error}</p>
              <p className="text-sm text-slate-400 mt-1">Verifique se o link está correto.</p>
            </div>
          )}

          {!loading && !error && submitted && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <span className="material-symbols-outlined text-green-600 text-3xl">check_circle</span>
              </div>
              <h2 className="text-lg font-bold text-slate-800">Dados enviados!</h2>
              <p className="text-sm text-slate-500 mt-2">
                Os dados de <strong>{getPeriodLabel()}</strong> foram registrados com sucesso.
              </p>
              {formData?.entry && (
                <div className="mt-5 bg-slate-50 rounded-xl p-4 text-left space-y-2">
                  <SummaryRow label="Leads" value={formData.entry.leads} />
                  <SummaryRow label="Agendamentos" value={formData.entry.appointments} />
                  <SummaryRow label="Comparecimentos" value={formData.entry.attendances} />
                  <SummaryRow label="Fechamentos" value={formData.entry.closings ?? 0} />
                </div>
              )}
            </div>
          )}

          {!loading && !error && !submitted && formData && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center gap-3">
                <span className="material-symbols-outlined text-blue-500 shrink-0">calendar_today</span>
                <div>
                  <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Período de Referência</p>
                  <p className="text-sm font-semibold text-blue-800">{getPeriodLabel()}</p>
                </div>
              </div>

              <FormInput
                label="Quantidade de Leads"
                icon="person_add"
                value={values.leads}
                onChange={v => setValues(p => ({ ...p, leads: v }))}
                placeholder="0"
              />
              <FormInput
                label="Quantidade de Agendamentos"
                icon="event"
                value={values.appointments}
                onChange={v => setValues(p => ({ ...p, appointments: v }))}
                placeholder="0"
              />
              <FormInput
                label="Quantidade de Comparecimentos"
                icon="how_to_reg"
                value={values.attendances}
                onChange={v => setValues(p => ({ ...p, attendances: v }))}
                placeholder="0"
              />
              <FormInput
                label="Quantidade de Fechamentos"
                icon="task_alt"
                value={values.closings}
                onChange={v => setValues(p => ({ ...p, closings: v }))}
                placeholder="0"
                required={false}
              />

              <button
                type="submit"
                disabled={submitting || !values.leads || !values.appointments || !values.attendances}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[20px]">send</span>
                    Enviar Dados
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        <div className="px-8 pb-5 text-center">
          <p className="text-[10px] text-slate-300 uppercase tracking-wider">Lumina Reporting • Agência MW</p>
        </div>
      </div>
    </div>
  );
}

function FormInput({ label, icon, value, onChange, placeholder, required = true }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">{icon}</span>
        <input
          type="number"
          min="0"
          required={required}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-slate-800">{value}</span>
    </div>
  );
}
